import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useCheckTemplateName } from "../api/queries/use-check-template-name";

const DEBOUNCE_MS = 600;

/**
 * Handles real-time availability validation for the template name.
 *
 * Returns:
 *  - `isChecking`  → true while waiting for debounce or the query is loading
 *  - `isAvailable` → true if the name is available (does not exist), null if not yet known
 *  - `isTaken`     → true if the name is already in use
 *  - `onNameChange`→ callback to connect to the input's onChange
 */
export const useTemplateNameAvailability = () => {
  const [debouncedName, setDebouncedName] = useState("");
  const [isDebouncing, setIsDebouncing] = useState(false);
  const timerRef = useRef(null);

  const channelId = useSelector((state) => state.campaign.create.channelId);

  const { isFetching, data: matches } = useCheckTemplateName(
    channelId,
    debouncedName,
  );

  const onNameChange = useCallback((rawName) => {
    // Clear previous timer
    if (timerRef.current) clearTimeout(timerRef.current);

    const trimmed = rawName?.trim() ?? "";

    if (trimmed.length < 3) {
      setDebouncedName("");
      setIsDebouncing(false);
      return;
    }

    setIsDebouncing(true);

    timerRef.current = setTimeout(() => {
      setDebouncedName(trimmed);
      setIsDebouncing(false);
    }, DEBOUNCE_MS);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const isChecking = isDebouncing || isFetching;
  const hasChecked = !isChecking && debouncedName.length >= 3;
  const isTaken = hasChecked && Array.isArray(matches) && matches.length > 0;
  const isAvailable = hasChecked && Array.isArray(matches) && matches.length === 0;

  return {
    isChecking,
    isAvailable,
    isTaken,
    onNameChange,
  };
};
